/* eslint-disable no-param-reassign,consistent-return,no-shadow */
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { message, Modal } from "antd";
import rtkRequestStatusCodes from "../../lib/const/rtkRequestStatusCodes";
import {
  createServiceTypeOrDeleteVersionRoute,
  createServiceTypesRoute,
  getServiceTypesRoute,
  getServiceTypesVersionsRoute,
  getTaxesRates,
  getTaxesSummary,
} from "../../lib/routes/services";
import Message from "../../components/Message/Message";
import { ServiceTypesTableProps } from "../../components/ServiceTypes/ServiceTypesInterfaces";
import { Characteristic } from "../../lib/types/Offer";
import { LoadingStatusCode } from "../../lib/types";

interface ICreateServiceData {
  id: number;
  fromDate: string;
  dataValues: {};
}

interface IDeleteServiceData {
  id: number;
  fromDate: string;
}

interface ITaxes {
  amount: string;
  rate: {
    id: number;
    rate: string;
    name: string;
  };
  type: {
    id: number;
    name: string;
  };
}

interface ITaxRates {
  dsc: string;
  fromDate: string;
  id: number;
  taxRate: number;
  taxRateId: number;
  taxRateName: string;
  taxTypeId: number;
  tenantId: number;
  toDate: string;
}

interface IServiceTypesData {
  baseServiceTypeId: number;
  chargeNameId: number;
  dsc: string;
  fromDate: string;
  id: number;
  name: string;
  productId: number;
  serviceCode: string;
  serviceModeId: number;
  taxRateMask: number;
  taxRates: ITaxRates[];
  taxTypeMask: number;
  taxes: ITaxes[];
  tenantId: number;
  toDate: string;
}

interface IDataState {
  serviceTypes: IServiceTypesData[] | undefined;
  serviceTypesLoading: LoadingStatusCode;
  serviceTypesVersions: IServiceTypesData[] | undefined;
  serviceTypesVersionsLoading: LoadingStatusCode;
  taxesRates: ITaxRates[];
  taxesRatesLoading: LoadingStatusCode;
  taxesSummary: ITaxes[];
  taxesSummaryLoading: LoadingStatusCode;
}

const initialState: IDataState = {
  serviceTypes: [],
  serviceTypesLoading: rtkRequestStatusCodes.idle,
  serviceTypesVersions: [],
  serviceTypesVersionsLoading: rtkRequestStatusCodes.idle,
  taxesRates: [],
  taxesRatesLoading: rtkRequestStatusCodes.idle,
  taxesSummary: [],
  taxesSummaryLoading: rtkRequestStatusCodes.idle,
};

const fetchServiceTypesAdmin = createAsyncThunk(
  "service-types/fetchServiceTypesAdmin",
  async (onDate?: string) => {
    try {
      const url = [getServiceTypesRoute(onDate)].join("");
      const response = axios.get(url);
      const { data } = await response;
      const dataTaxes: IServiceTypesData[] = await Promise.all(
        data.map(async (item: ServiceTypesTableProps) => {
          try {
            const date = item.toDate;
            const response = await axios.get([getTaxesSummary(item.id, date)].join(""));
            item.taxes = response?.data?.taxes;
          } catch (e) {
            // @ts-ignore
            throw new Error(e?.response?.data.message);
          }
          return item;
        })
      );
      return dataTaxes.sort((a: { name: string }, b: { name: string }) =>
        a.name > b.name ? 1 : -1
      );
    } catch (e) {
      Message("Ошибка загрузки данных. Попробуйте позже", e);
    }
  }
);

const fetchServiceTypesVersions = createAsyncThunk(
  "service-types/fetchServiceTypesVersions",
  async (id: number) => {
    const response = axios.get(getServiceTypesVersionsRoute(id));
    const { data } = await response;
    const dataTaxes: IServiceTypesData[] = await Promise.all(
      data.map(async (item: ServiceTypesTableProps) => {
        try {
          const date = item.toDate;
          const response = await axios.get([getTaxesSummary(item.id, date)].join(""));
          item.taxes = response?.data?.taxes;
        } catch (e) {
          // @ts-ignore
          throw new Error(e?.response?.data.message);
        }
        return item;
      })
    );
    return dataTaxes.sort(
      (a: { toDate: string }, b: { toDate: string }) =>
        new Date(b.toDate).getTime() - new Date(a.toDate).getTime()
    );
  }
);

const createServiceTypes = createAsyncThunk(
  "service-types/createServiceTypesRoute",
  async (dataPost: ServiceTypesTableProps & { info?: Characteristic[] }) => {
    try {
      message.loading("Сохранение", 0);
      const response = axios.post(createServiceTypesRoute(), dataPost);
      const { data } = await response;
      message.destroy();
      message.success("Тип продукта успешно добавлен");
      return data;
    } catch (e) {
      message.destroy();
      Message("Ошибка создания", e);
    }
  }
);

const createServiceTypeVersion = createAsyncThunk(
  "service-types/createServiceTypeVersion",
  async (dataPost: ICreateServiceData) => {
    const { id, fromDate, dataValues } = dataPost;
    try {
      message.loading("Сохранение", 0);
      const response = axios.patch(createServiceTypeOrDeleteVersionRoute(id, fromDate), dataValues);
      const { data } = await response;
      message.destroy();
      message.success("Версия успешно добавлена");
      return data;
    } catch (e) {
      message.destroy();
      message.error("Ошибка обновления");
    }
  }
);

const deleteServiceTypeVersion = createAsyncThunk(
  "service-types/deleteServiceTypeVersion",
  async (dataPost: IDeleteServiceData) => {
    const { id, fromDate } = dataPost;
    try {
      message.loading("Изменение даты", 0);
      const response = axios.delete(createServiceTypeOrDeleteVersionRoute(id, fromDate));
      const { data } = await response;
      message.destroy();
      message.success("Дата успешно обновлёна");
      return data;
    } catch (e) {
      message.destroy();
      Modal.error({
        title: "Ошибка",
        // @ts-ignore
        content: `${e.response.data.message}`,
      });
    }
  }
);

const fetchTaxesRates = createAsyncThunk("service-types/fetchTaxesRates", async (onDate) => {
  const url = [getTaxesRates(onDate)].join("");

  const response = axios.get(url);
  const { data } = await response;
  return data;
});

const fetchTaxesSummary = createAsyncThunk(
  "service-types/fetchTaxesSummary",
  async (id: number) => {
    const url = [getTaxesSummary(id)].join("");
    const response = axios.get(url);
    const { data } = await response;
    return data;
  }
);

export const slice = createSlice({
  name: "serviceTypes",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchServiceTypesAdmin.fulfilled, (state, action) => {
        state.serviceTypes = action.payload;
        state.serviceTypesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchServiceTypesAdmin.pending, (state) => {
        state.serviceTypesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchServiceTypesVersions.fulfilled, (state, action) => {
        state.serviceTypesVersions = action.payload;
        state.serviceTypesVersionsLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchServiceTypesVersions.pending, (state) => {
        state.serviceTypesVersionsLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchTaxesRates.fulfilled, (state, action) => {
        state.taxesRates = action.payload;
        state.taxesRatesLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchTaxesRates.pending, (state) => {
        state.taxesRatesLoading = rtkRequestStatusCodes.pending;
      })
      .addCase(fetchTaxesSummary.fulfilled, (state, action) => {
        state.taxesSummary = action.payload;
        state.taxesSummaryLoading = rtkRequestStatusCodes.fulfilled;
      })
      .addCase(fetchTaxesSummary.pending, (state) => {
        state.taxesSummaryLoading = rtkRequestStatusCodes.pending;
      });
  },
});

const { reducer } = slice;

export {
  fetchServiceTypesAdmin,
  fetchServiceTypesVersions,
  createServiceTypes,
  createServiceTypeVersion,
  deleteServiceTypeVersion,
  fetchTaxesRates,
  fetchTaxesSummary,
};

export default reducer;
